import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronRight } from "lucide-react";
import swal from "sweetalert";
import { TableSkeletonRows } from "../loader";
import { aiService } from "../../service/api.service";
import AddKnowledgeBaseModal from "./AddKnowledgeBaseModal";
import { frontendUrl } from "../../config/config";
import { TablePagination } from "../common/TablePagination";
import { EmptyTableState } from "../common/EmptyTableState";
import { AgentStatusBadge } from "../common/StatusBadge";
import {
  StyledTable,
  StyledTableHeader,
  StyledTableCell,
  StyledTableRow,
} from "../common/StyledTable";

const TONE_OPTIONS = ["Friendly", "Professional", "Formal", "Casual"];
const STATUS_OPTIONS = ["enabled", "disabled", "deleted"];

export default function AgentList({ allAgentsView = false }) {
  const [loader, setLoader] = useState(false);
  const [rowActionLoading, setRowActionLoading] = useState({});
  const [agents, setAgents] = useState([]);
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [toneFilter, setToneFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [knowledgeBaseModal, setKnowledgeBaseModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [copiedAgentId, setCopiedAgentId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectUserModal, setSelectUserModal] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [selectedCreateUserId, setSelectedCreateUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [userPage, setUserPage] = useState(1);
  const page_size = 10;
  const user_page_size = 100;
  const navigate = useNavigate();
  const location = useLocation();
  const isAllAgentsView = allAgentsView;

  const getAgentList = useCallback(async () => {
    if (!isAllAgentsView && !userId) {
      setAgents([]);
      setTotalItems(0);
      return;
    }

    setLoader(true);
    try {
      const res = await aiService.getAgents({
        user_id: userId,
        all: isAllAgentsView,
        page,
        page_size,
        // Blank filter shows everything, including deleted agents (so they
        // can be restored from this same list); "all" tells the backend
        // that's intentional, as opposed to no status param at all.
        status: statusFilter || "all",
        search: debouncedSearch,
        tone: toneFilter,
      });
      setAgents(res?.data?.data || []);
      setTotalItems(res?.data?.total_count || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      if (error?.response?.status === 401) {
        swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
          localStorage.clear();
          window.location.href = "/";
        });
      } else {
        swal({ icon: "error", text: "Failed to load agents." });
      }
    }
  }, [isAllAgentsView, page, userId, page_size, statusFilter, debouncedSearch, toneFilter]);

  const getUserList = useCallback(async () => {
    setUserListLoading(true);
    try {
      const res = await aiService.userList({
        search: debouncedUserSearch,
        limit: user_page_size,
        page: userPage,
      });
      setUserList(res?.data?.data || []);
    } catch (error) {
      if (error?.response?.status === 401) {
        swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
          localStorage.clear();
          window.location.href = "/";
        });
      } else {
        swal({ icon: "error", text: "Failed to load users." });
      }
    } finally {
      setUserListLoading(false);
    }
  }, [debouncedUserSearch, userPage]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const selectedUserId =
      location.state?.userId || searchParams.get("user_id");
    const selectedUserName =
      location.state?.userName || searchParams.get("user_name") || "";

    if (isAllAgentsView) {
      setUserId(null);
      setUserName("");
      return;
    }

    if (selectedUserId) {
      setUserId(selectedUserId);
      setUserName(selectedUserName);
    } else {
      swal({
        icon: "error",
        text: "Please select a user to view their agents.",
      }).then(() => {
        navigate("/company-list");
      });
    }
  }, [isAllAgentsView, location, navigate]);

  useEffect(() => {
    getAgentList();
  }, [getAgentList]);

  useEffect(() => {
    const status = new URLSearchParams(location.search).get("status");
    setStatusFilter(STATUS_OPTIONS.includes(status) ? status : "");
  }, [location.search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedUserSearch(userSearch);
    }, 300);

    return () => clearTimeout(handler);
  }, [userSearch]);

  useEffect(() => {
    if (selectUserModal) {
      getUserList();
    }
  }, [getUserList, selectUserModal]);

  function handlePageChange(pageNumber) {
    setPage(pageNumber);
  }

  function handleStatusFilterChange(value) {
    setStatusFilter(value);
    setPage(1);
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set("status", value);
    } else {
      params.delete("status");
    }
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true },
    );
  }

  const editAgent = (agent) => {
    navigate("/agent-create", {
      state: {
        userId: agent.user || userId,
        userName: agent.owner_name || userName,
        agentName: agent.name,
        agentId: agent._id,
        returnToAllAgents: isAllAgentsView,
      },
    });
  };

  const openAgentInfo = (agent) => {
    const query = new URLSearchParams({ agent_id: agent._id });
    if (agent.name) query.append("agent_name", agent.name);
    if (agent.user || userId) query.append("user_id", agent.user || userId);
    if (agent.owner_name || userName)
      query.append("user_name", agent.owner_name || userName);
    if (agent.owner_email) query.append("user_email", agent.owner_email);
    if (isAllAgentsView) query.append("from", "all");

    navigate(`/agent-info?${query.toString()}`, {
      state: {
        agent,
        agentId: agent._id,
        agentName: agent.name,
        userId: agent.user || userId,
        userName: agent.owner_name || userName,
        userEmail: agent.owner_email || "",
        returnToAllAgents: isAllAgentsView,
      },
    });
  };

  const createAgent = () => {
    if (isAllAgentsView) {
      setSelectUserModal(true);
      setSelectedCreateUserId("");
      setUserSearch("");
      setDebouncedUserSearch("");
      setUserPage(1);
      return;
    }

    navigate("/agent-create", {
      state: {
        userId,
        userName,
        returnToAllAgents: isAllAgentsView,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const closeSelectUserModal = () => {
    setSelectUserModal(false);
    setSelectedCreateUserId("");
  };

  const createAgentForSelectedUser = () => {
    const selectedUser = userList.find((u) => u._id === selectedCreateUserId);

    if (!selectedUser) {
      swal({ icon: "error", text: "Please select a user first." });
      return;
    }

    navigate("/agent-create", {
      state: {
        userId: selectedUser._id,
        userName: selectedUser.name || selectedUser.email || "",
        returnToAllAgents: true,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const openKnowledgeBaseModal = (agent) => {
    setSelectedAgent(agent);
    setKnowledgeBaseModal(true);
  };

  const openKnowledgeBaseList = (agent) => {
    navigate("/agent-knowledgebase", {
      state: {
        agent,
        agentName: agent.name,
        userId,
        userName,
        returnToAllAgents: isAllAgentsView,
      },
    });
  };

  const handleCopyWidgetLink = (agent) => {
    const url = `${frontendUrl}chatwidget/?secretKey=${agent?._id}&agentName=${encodeURIComponent(agent?.name || "")}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedAgentId(agent._id);
        setTimeout(() => setCopiedAgentId(null), 1500);
      })
      .catch((err) => {
        console.error("Failed to copy widget link: ", err);
      });
  };

  const closeKnowledgeBaseModal = () => {
    setKnowledgeBaseModal(false);
    setSelectedAgent(null);
  };

  const toggleAgentStatus = (agent) => {
    const isEnabled = agent.status !== "Disabled";
    const nextStatus = isEnabled ? "Disabled" : "Enabled";
    swal({
      title: "Confirmation",
      text: `Are you sure you want to ${isEnabled ? "disable" : "enable"} the agent "${agent.name}"?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (val) {
        setRowActionLoading((prev) => ({ ...prev, [agent._id]: true }));
        try {
          await aiService.changeAgentStatus(agent._id, { status: nextStatus });
          swal({
            icon: "success",
            text: `Agent ${isEnabled ? "disabled" : "enabled"} successfully.`,
          });
          await getAgentList();
        } catch (error) {
          if (error?.response?.status === 401) {
            swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(
              () => {
                localStorage.clear();
                window.location.href = "/";
              },
            );
          } else {
            swal({
              icon: "error",
              text: error?.response?.data?.message || "Failed to update agent status.",
            });
          }
        } finally {
          setRowActionLoading((prev) => ({ ...prev, [agent._id]: false }));
        }
      }
    });
  };

  const handleDeleteAgent = (agent) => {
    swal({
      title: "Confirmation",
      text: `Are you sure you want to delete the agent "${agent.name}"? It will be hidden everywhere until it's restored.`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setRowActionLoading((prev) => ({ ...prev, [agent._id]: true }));
      try {
        await aiService.deleteAgent(agent._id);
        swal({ icon: "success", text: "Agent deleted successfully." });
        await getAgentList();
      } catch (error) {
        if (error?.response?.status === 401) {
          swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
            localStorage.clear();
            window.location.href = "/";
          });
        } else {
          swal({
            icon: "error",
            text: error?.response?.data?.message || "Failed to delete agent.",
          });
        }
      } finally {
        setRowActionLoading((prev) => ({ ...prev, [agent._id]: false }));
      }
    });
  };

  const handleRestoreAgent = (agent) => {
    swal({
      title: "Confirmation",
      text: `Restore the agent "${agent.name}"?`,
      icon: "warning",
      buttons: ["NO", "YES"],
    }).then(async (val) => {
      if (!val) return;
      setRowActionLoading((prev) => ({ ...prev, [agent._id]: true }));
      try {
        await aiService.restoreAgent(agent._id);
        swal({ icon: "success", text: "Agent restored successfully." });
        await getAgentList();
      } catch (error) {
        if (error?.response?.status === 401) {
          swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
            localStorage.clear();
            window.location.href = "/";
          });
        } else {
          swal({
            icon: "error",
            text: error?.response?.data?.message || "Failed to restore agent.",
          });
        }
      } finally {
        setRowActionLoading((prev) => ({ ...prev, [agent._id]: false }));
      }
    });
  };

  function handleToneFilterChange(value) {
    setToneFilter(value);
    setPage(1);
  }

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div>
                <h1 className="company-page-title">
                  {isAllAgentsView ? "Created Agents" : "Created Agents"}
                </h1>
                <h5 className="company-page-subtitle">
                  {agents.length} of {totalItems} agents
                </h5>
              </div>
            </div>
            <div className="header-actions-right">
              <Button
                variant="primary"
                className="agent-primary-btn"
                onClick={createAgent}
              >
                Create Agent
              </Button>
            </div>
          </div>
          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="premium-filter-card">
                <div className="search-top-right">
                  <i className="fa fa-search" aria-hidden="true"></i>
                  <Form.Control
                    placeholder="Search by agent name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="filter-select"
                  aria-label="Tone"
                  value={toneFilter}
                  onChange={(e) => handleToneFilterChange(e.target.value)}
                >
                  <option value="">Tone</option>
                  {TONE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <select
                  className="filter-select"
                  aria-label="Status"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <option value="">Status</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                  <option value="deleted">Deleted</option>
                </select>
              </div>
              <div className="audit-request-box premium-company-card">
                <StyledTable
                  responsive="sm"
                  className={`UserListTable premium-user-list-table ${isAllAgentsView ? "all-agent-list-table" : "agent-list-table"}`}
                >
                  <colgroup>
                    {isAllAgentsView ? (
                      <>
                        <col className="agent-name-col" />
                        <col className="agent-owner-col" />
                        <col className="agent-tone-col" />
                        <col className="agent-status-col" />
                        <col className="agent-action-col" />
                      </>
                    ) : (
                      <>
                        <col className="agent-name-col" />
                        <col className="agent-purpose-col" />
                        <col className="agent-tone-col" />
                        <col className="agent-status-col" />
                        <col className="agent-action-col" />
                      </>
                    )}
                  </colgroup>
                  <thead>
                    <StyledTableRow>
                      <StyledTableHeader>Name</StyledTableHeader>
                      {isAllAgentsView ? (
                        <StyledTableHeader>Owned By</StyledTableHeader>
                      ) : null}
                      {!isAllAgentsView ? (
                        <StyledTableHeader>Purpose</StyledTableHeader>
                      ) : null}
                      <StyledTableHeader>Tone</StyledTableHeader>
                      <StyledTableHeader>Status</StyledTableHeader>
                      <StyledTableHeader>Actions</StyledTableHeader>
                    </StyledTableRow>
                  </thead>
                  <tbody>
                    {loader ? (
                      <TableSkeletonRows rows={page_size} columns={5} />
                    ) : agents.length > 0 ? (
                      agents.map((a) => {
                        const isEnabled = a.status !== "Disabled";
                        const isDeleted = !!a.is_deleted;
                        const statusCell = isDeleted ? (
                          <div className="agent-status-cell">
                            <AgentStatusBadge isDeleted />
                          </div>
                        ) : (
                          <div className="agent-status-cell">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {!isEnabled && a.disabledBy === "admin"
                                    ? "Disabled by administrator"
                                    : `Click to ${isEnabled ? "disable" : "enable"} agent`}
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="switch"
                                id={`agent-status-switch-${a._id}`}
                                checked={isEnabled}
                                disabled={rowActionLoading[a._id]}
                                onChange={() => toggleAgentStatus(a)}
                              />
                            </OverlayTrigger>
                          </div>
                        );

                        const actionButtons = isDeleted ? (
                          <div className="agent-action-buttons">
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="agent-action-btn-labeled"
                              title="Restore Agent"
                              disabled={rowActionLoading[a._id]}
                              onClick={() => handleRestoreAgent(a)}
                            >
                              <i className="fa fa-undo" aria-hidden="true"></i> Restore
                            </Button>
                            <button
                              type="button"
                              className="user-row-chevron-btn"
                              aria-label={`View profile for ${a.name || "agent"}`}
                              title="View Agent Profile"
                              onClick={() => openAgentInfo(a)}
                            >
                              <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="agent-action-buttons">
                              <Button
                                size="sm"
                                variant="light"
                                title="Add Knowledge Base"
                                onClick={() => openKnowledgeBaseModal(a)}
                              >
                                <i
                                  className="fa fa-plus"
                                  aria-hidden="true"
                                ></i>
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                title="See Knowledgebase"
                                onClick={() => openKnowledgeBaseList(a)}
                              >
                                <i className="fa fa-eye" aria-hidden="true"></i>
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                title="Copy Widget Link"
                                onClick={() => handleCopyWidgetLink(a)}
                              >
                                <i
                                  className="fa fa-link"
                                  aria-hidden="true"
                                ></i>
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                title="Edit Agent"
                                onClick={() => editAgent(a)}
                              >
                                <i
                                  className="fa fa-pencil"
                                  aria-hidden="true"
                                ></i>
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                title="Delete Agent"
                                disabled={rowActionLoading[a._id]}
                                onClick={() => handleDeleteAgent(a)}
                              >
                                <i className="fa fa-trash text-danger" aria-hidden="true"></i>
                              </Button>
                              <button
                                type="button"
                                className="user-row-chevron-btn"
                                aria-label={`View profile for ${a.name || "agent"}`}
                                title="View Agent Profile"
                                onClick={() => openAgentInfo(a)}
                              >
                                <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
                              </button>
                            </div>
                            {copiedAgentId === a._id && (
                              <div className="text-success agent-copy-message">
                                Widget link copied!
                              </div>
                            )}
                          </>
                        );

                        return isAllAgentsView ? (
                          <StyledTableRow
                            key={a._id}
                            className={isDeleted ? "agent-row-deleted" : !isEnabled ? "agent-row-disabled" : ""}
                          >
                            <StyledTableCell>
                              <button
                                type="button"
                                className="user-name-link agent-name-link"
                                onClick={() => openAgentInfo(a)}
                              >
                                {a.name}
                              </button>
                            </StyledTableCell>
                            <StyledTableCell>
                              <div className="agent-owner-cell">
                                <span>
                                  {a.owner_name ||
                                    a.owner_email ||
                                    "Unknown user"}
                                </span>
                                {a.owner_name && a.owner_email ? (
                                  <small>{a.owner_email}</small>
                                ) : null}
                              </div>
                            </StyledTableCell>
                            <StyledTableCell>{a.tone}</StyledTableCell>
                            <StyledTableCell>{statusCell}</StyledTableCell>
                            <StyledTableCell>{actionButtons}</StyledTableCell>
                          </StyledTableRow>
                        ) : (
                          <StyledTableRow
                            key={a._id}
                            className={isDeleted ? "agent-row-deleted" : !isEnabled ? "agent-row-disabled" : ""}
                          >
                            <StyledTableCell>
                              <button
                                type="button"
                                className="user-name-link agent-name-link"
                                onClick={() => openAgentInfo(a)}
                              >
                                {a.name}
                              </button>
                            </StyledTableCell>
                            <StyledTableCell>{a.purpose}</StyledTableCell>
                            <StyledTableCell>{a.tone}</StyledTableCell>
                            <StyledTableCell>{statusCell}</StyledTableCell>
                            <StyledTableCell>{actionButtons}</StyledTableCell>
                          </StyledTableRow>
                        );
                      })
                    ) : (
                      <EmptyTableState colSpan={5} />
                    )}
                  </tbody>
                </StyledTable>
                <TablePagination
                  activePage={page}
                  itemsCountPerPage={page_size}
                  totalItemsCount={totalItems}
                  onChange={handlePageChange}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <AddKnowledgeBaseModal
        show={knowledgeBaseModal}
        handleClose={closeKnowledgeBaseModal}
        agent={selectedAgent}
      />

      <Modal
        show={selectUserModal}
        onHide={closeSelectUserModal}
        className="premium-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Select the user for which you want to create the agent
            </Form.Label>
            <div className="search-top-right">
              <Form.Control
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserPage(1);
                  setSelectedCreateUserId("");
                }}
              />
              <i className="fa fa-search" aria-hidden="true"></i>
            </div>
          </Form.Group>

          <div className="agent-user-select-list">
            <StyledTable
              responsive="sm"
              className="UserListTable premium-user-list-table agent-user-select-table"
            >
              <thead>
                <StyledTableRow>
                  <StyledTableHeader>Select</StyledTableHeader>
                  <StyledTableHeader>Name</StyledTableHeader>
                  <StyledTableHeader>Email</StyledTableHeader>
                </StyledTableRow>
              </thead>
              <tbody>
                {userListLoading ? (
                  <TableSkeletonRows rows={user_page_size} columns={3} />
                ) : userList.length > 0 ? (
                  userList.map((u) => (
                    <StyledTableRow
                      key={u._id}
                      className={
                        selectedCreateUserId === u._id
                          ? "agent-user-select-row selected"
                          : "agent-user-select-row"
                      }
                      onClick={() => setSelectedCreateUserId(u._id)}
                    >
                      <StyledTableCell>
                        <Form.Check
                          type="radio"
                          name="createAgentUser"
                          aria-label={`Select ${u.name || u.email || "user"}`}
                          checked={selectedCreateUserId === u._id}
                          readOnly
                          style={{ pointerEvents: "none" }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {u.name || "Unknown user"}
                      </StyledTableCell>
                      <StyledTableCell>{u.email || "-"}</StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <EmptyTableState colSpan={3} />
                )}
              </tbody>
            </StyledTable>
          </div>

          <div className="d-flex gap-2 mt-4">
            <Button
              variant="primary"
              type="button"
              disabled={!selectedCreateUserId || userListLoading}
              onClick={createAgentForSelectedUser}
            >
              Create Agent
            </Button>
            <Button
              variant="secondary"
              type="button"
              disabled={userListLoading}
              onClick={closeSelectUserModal}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
